/**
 * @fileoverview WelcomeHeader Component - Dynamic page header with context-aware content
 * 
 * Provides dynamic page headers that adapt to current route and content:
 * - Dynamic title updates based on current page/route
 * - News article title loading from API
 * - Event title loading from API  
 * - Working group name resolution
 * - Media centre series name handling
 * - Daire AI assistant integration
 * - ETF Intelligence chat for ETFBook page
 * - Weekly news filtering and display
 * - Bounce animations and visual feedback
 * 
 * @component
 * @example
 * <WelcomeHeader />
 * 
 * @requires React
 * @requires react-router-dom
 * @requires html-react-parser
 * @requires ../Daire/DaireSidebar
 * @requires ../Daire/ETFDaireChat
 * @requires ../../utils/apiHelper
 * @requires ../../utils/textUtils
 * @requires @tippyjs/react
 * @requires @auth0/auth0-react
 * @requires ../../utils/constants
 * 
 * @author By John Bellew - Infinia.ie - last updated 10/07/2025
 */

import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import Parser from 'html-react-parser';
import DaireSidebar from '../Daire/DaireSidebar';
import ETFDaireChat from '../Daire/ETFDaireChat';
import apiHelper from '../../utils/apiHelper';
import { getLastSpecificDay, getToday } from '../../utils/textUtils';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { AuthContext } from '../Auth/AuthContext';

const { FILE_NAMES } = require('../../utils/constants');

// First, let's define some route patterns with regex
const ROUTE_PATTERNS = [
  {
    // Will match both "Members/Profile" and "Members/Profile/454"
    pattern: /^members\/memberprofile(?:\/\d+)?$/i,  
    config: {
      text: 'Member Profile',
      imageClass: '',
      titleClass: 'left-bottom-title'
    }
  },
  {
    pattern: /^workinggroups\/workinggroup\/.+$/i,
    config: {
      text: 'Working Group',
      imageClass: 'welcome-header-workinggroups',
      titleClass: 'left-bottom-title'
    }
  },
  {
    pattern: /^workinggroups\/steeringgroup\/.+$/i,
    config: {
      text: 'Steering Group',
      imageClass: 'welcome-header-steeringgroups',
      titleClass: 'left-bottom-title'
    }
  },
  {
    pattern: /^capabilityinnovation\/capability\/(\d+)$/i,
    config: {
      text: 'Capability',
      imageClass: '',
      titleClass: 'left-bottom-title'
    }
  },
  {
    pattern: /^news\/(\d+)$/i,
    config: {
      text: 'News Article',
      imageClass: 'welcome-header-news',
      titleClass: 'left-bottom-title'
    }
  },
  {
    pattern: /^events\/(\d+)$/i,
    config: {
      text: 'Event',
      imageClass: 'welcome-header-events',
      titleClass: 'left-bottom-title'
    }
  },
  {
    pattern: /^mediacentre\/series\/(.+)$/i,
    config: {
      text: 'Media Centre Series',
      imageClass: 'welcome-header-media-centre',
      titleClass: 'left-bottom-title'
    }
  }
];


const ROUTE_MAPPINGS = {
  '': { text: 'Irish Funds Member Portal', imageClass: 'welcome-header-spotlight' },
  'spotlight': { text: 'Irish Funds Member Portal', imageClass: 'welcome-header-spotlight' },
  'file': { text: 'Loading News Article...', imageClass: 'welcome-header-news' },
  'policyregulatory': { text: 'Policy & Regulatory', imageClass: 'welcome-header-policyregulatory' },
  'newsroom': { text: 'News Room', imageClass: 'welcome-header-news' },
  'representation': { text: 'Representation', imageClass: 'welcome-header-representation' },
  'events': { text: 'Events', imageClass: 'welcome-header-events' },
  'workinggroups': { text: 'Working Groups', imageClass: 'welcome-header-workinggroups' },
  'councilcorner': { text: 'Council Corner', imageClass: 'welcome-header-councilcorner' },
  'trainingdevelopment': { text: 'Training & Development', imageClass: 'welcome-header-trainingdevelopment' },
  'capabilityinnovation': { text: 'Capability & Innovation', imageClass: 'welcome-header-capabilityinnovation' },
  'members': { text: 'Members', imageClass: 'welcome-header-members' },
  'operations': { text: 'Operations', imageClass: 'welcome-header-operations' },
  'factsfigures': { text: 'Facts & Figures', imageClass: 'welcome-header-factsfigures' },
  'support': { text: 'Support', imageClass: 'welcome-header-support' },
  'policyregulatory/keypolicybench': { text: 'Key Policy Messages', imageClass: 'welcome-header-keypolicybench' },
  'policyregulatory/cbibench': { text: 'CBI Quarterly Update', imageClass: 'welcome-header-cbibench' },
  'profile': { text: 'Your Profile', imageClass: 'welcome-header-profile' },
  'search': { text: 'Search Centre', imageClass: 'welcome-header-search' },
  'daire': { text: 'Ask Daire!', imageClass: 'welcome-header-daire' },
  'mediacentre': { text: 'Media Centre', imageClass: 'welcome-header-media-centre' },
  'etfbook': { text: 'ETF Screener', subtitle: 'Powered by <img src="/assets/img/etf-dark.png" alt="ETFBook" style="width: 110px; vertical-align: bottom;">', imageClass: 'welcome-header-etfbook' },
  
};

/**
 * WelcomeHeader Component
 * 
 * Renders a dynamic page header that adapts to the current route and content.
 * Provides context-aware titles, background images, and integrated AI assistant access.
 * 
 * Features:
 * - Dynamic title resolution based on current route
 * - API integration for news articles, events, and working groups
 * - Cached title optimization for performance
 * - Daire AI assistant sidebar integration
 * - ETF Intelligence chat for ETFBook page
 * - Weekly news filtering and display
 * - Bounce animations and visual feedback
 * - Profile data integration
 * - Loading states for dynamic content
 * 
 * @component
 * @returns {JSX.Element} Dynamic welcome header with context-aware content
 */
function WelcomeHeader() {
  const { user: [user] } = useContext(AuthContext);
  const [welcomeText, setWelcomeText] = useState('');
  const [welcomeSubtitle, setWelcomeSubtitle] = useState('');
  const [welcomeImageClass, setWelcomeImageClass] = useState('');
  const [isLoadingTitle, setIsLoadingTitle] = useState(false);
  const location = useLocation();
  const cleanedPath = location.pathname.endsWith('/')
    ? location.pathname.slice(0, -1).toLowerCase()
    : location.pathname.toLowerCase();

  let titleClass = "left-bottom-title";
  const [bounce, setBounce] = useState(false);
  const [isDaireSidebarOpen, setIsDaireSidebarOpen] = useState(false);
  const [isDaireSidebarMinimized, setIsDaireSidebarMinimized] = useState(false);
  const [isETFChatOpen, setIsETFChatOpen] = useState(false);
  const [weelklyNewsData, setWeeklyNewsData] = useState([]);
  const [weelklyfilteredNews, setWeelklyFilteredNews] = useState([]);
  

  const filterNewsFromSpecificDay = (data, dayOfWeek) => {
    const lastSpecificDay = getLastSpecificDay(dayOfWeek);
    const today = getToday();
    return data.filter(newsItem => {
      const [day, month, year] = newsItem.issueDate.split('/').map(Number);
      const newsDate = new Date(year, month - 1, day);
      return newsDate >= lastSpecificDay && newsDate <= today;
    });
  };

  useEffect(() => {
    const thisWeeksNews = filterNewsFromSpecificDay(weelklyNewsData, 5);
    const intro = thisWeeksNews.find(newsItem => newsItem.weeklyCategory === 'Intro');
    if (intro) {
      intro.image = "/assets/img/news/weekly/Pat.png";
    }
    const nonIntroNews = thisWeeksNews.filter(newsItem => newsItem.weeklyCategory !== 'Intro');
    setWeelklyFilteredNews(nonIntroNews);
  }, [weelklyNewsData]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Use graph API for real-time news data instead of JSON files
        const response = await apiHelper.fetchJSONData('getAllNews');
        if (response && response.news && Array.isArray(response.news)) {
          setWeeklyNewsData(response.news);
        } else {
          console.error('Invalid response from getAllNews API:', response);
        }
      } catch (err) {
        // Handle Auth0 not ready gracefully
        if (err.message === 'AUTH0_NOT_READY') {
          console.warn('Auth0 not ready, will retry news fetch later');
          // Could implement a retry mechanism here if needed
          return;
        }
        console.error('Error fetching news data from graph API:', err.message);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const updateWelcomeText = async () => {
      const path = cleanedPath.replace(/^\/+|\/+$/g, '');
      
      // Reset loading state for non-news pages
      if (!path.match(/^news\/(?:\w+\/)?(\d+)$/i)) {
        setIsLoadingTitle(false);
      }
     
      // Check if we're on a member profile page
      if (path === 'profile') {
        try {
          const profileData = await apiHelper.getMemberProfileByEmail(user.profile.email);
          if (profileData) {
            setWelcomeText(profileData.name);
            setWelcomeSubtitle('');
            setWelcomeImageClass(''); 
            return;
          }
        } catch (err) {
          console.error('Error fetching member data:', err);
        }
      }
      
      // Check if we're on a working group page
      const workingGroupMatch = path.match(/^workinggroups\/workinggroup\/(\d+)$/i);
      if (workingGroupMatch) {
        try {
          const wgDataResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.WG_STRUCTURE);
          const groupId = parseInt(workingGroupMatch[1]);
          const group = wgDataResult.fileContent.find(g => g.id === groupId);
          if (group) {
            setWelcomeText(group.workingGroupName);
            setWelcomeSubtitle('');
            setWelcomeImageClass('welcome-header-workinggroups');
            return;
          }
        } catch (err) {
          console.error('Error fetching working group data:', err);
        }
      }

      // Check if we're on a media centre series page
      const seriesMatch = path.match(/^mediacentre\/series\/(.+)$/i);
      if (seriesMatch) {
        try {
          // Use original pathname to preserve case for series name
          const originalPath = location.pathname.replace(/^\/+|\/+$/g, '');
          const originalSeriesMatch = originalPath.match(/^MediaCentre\/series\/(.+)$/i);
          const seriesNameRaw = originalSeriesMatch ? originalSeriesMatch[1] : seriesMatch[1];
          
          // Decode the URL-encoded series name
          const seriesName = decodeURIComponent(seriesNameRaw);
          // Don't add "Series" if it's already in the name (case insensitive check)
          const displayName = seriesName.toLowerCase().includes('series') 
            ? seriesName 
            : `${seriesName} Series`;
          setWelcomeText(displayName);
          setWelcomeSubtitle('');
          setWelcomeImageClass('welcome-header-media-centre');
          return;
        } catch (err) {
          console.error('Error decoding series name:', err);
          setWelcomeText('Media Centre Series');
          setWelcomeSubtitle('');
          setWelcomeImageClass('welcome-header-media-centre');
          return;
        }
      }

      // Check if we're on a file page (both legacy and new formats)
      const fileMatch = path.match(/^(?:file|files\/detail\/.+)$/i);
      if (fileMatch) {
        // Show a generic document loading message for file routes
        setWelcomeText('Loading News Article...');
        setWelcomeImageClass('welcome-header-news'); // Using news header style for documents
        setIsLoadingTitle(true);
        return;
      }

      // Check if we're on an events page
      const eventsMatch = path.match(/^events\/(\d+)$/i);
      if (eventsMatch) {
        const eventId = eventsMatch[1];
        
        // Check if we have a cached title from navigation (EventsModal)
        const cachedTitle = sessionStorage.getItem(`event_title_${eventId}`);
        
        if (cachedTitle) {
          // Use cached title immediately, no loading state needed
          setWelcomeText(cachedTitle);
          setWelcomeImageClass('welcome-header-events');
          setIsLoadingTitle(false);
          
          // Clean up the cached title since we've used it
          sessionStorage.removeItem(`event_title_${eventId}`);
          return;
        }
        
        // No cached title, set loading state and fetch from API
        setWelcomeText('Loading Event...');
        setWelcomeImageClass('welcome-header-events');
        setIsLoadingTitle(true);
        
        try {
          const eventsResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.EVENTS);
          const events = eventsResult.fileContent;
          
          // Find the event by ID
          const event = events.find(e => e.ID && e.ID.toString() === eventId);
          
          if (event) {
            setWelcomeText(event.Title);
            setIsLoadingTitle(false);
          } else {
            setWelcomeText('Event Not Found');
            setIsLoadingTitle(false);
          }
          return;
        } catch (err) {
          console.error('Error fetching event data:', err);
          setWelcomeText('Error Loading Event');
          setIsLoadingTitle(false);
        }
      }

      // Check if we're on a news page (both new and legacy formats)
      const newsMatch = path.match(/^news\/(?:(\w+)\/)?(\d+)$/i);
      if (newsMatch) {
        const articleType = newsMatch[1]; // Could be undefined for legacy URLs
        const newsId = newsMatch[2]; // Article ID is now in position 2
        
        // Check if we have a cached title from navigation (NewsModal)
        const cachedTitle = sessionStorage.getItem(`news_title_${newsId}`);
        
        if (cachedTitle) {
          // Use cached title immediately, no loading state needed
          setWelcomeText(cachedTitle);
          setWelcomeImageClass('welcome-header-news');
          setIsLoadingTitle(false);
          
          // Clean up the cached title since we've used it
          sessionStorage.removeItem(`news_title_${newsId}`);
          return;
        }
        
        // No cached title, set loading state and fetch from API
        setWelcomeText('Loading News Article...');
        setWelcomeImageClass('welcome-header-news');
        setIsLoadingTitle(true);
        
        try {
          // Use graph API for real-time news data
          const response = await apiHelper.fetchJSONData('getAllNews');
          let allNews = [];
          
          if (response && response.news && Array.isArray(response.news)) {
            allNews = response.news;
          } else {
            console.warn('Invalid response from getAllNews API:', response);
          }
          
          // Find the news item with type checking if specified
          const newsItem = allNews.find(item => {
            const itemId = item.id;
            const searchId = newsId;
            
            // First check ID match
            let idMatches = false;
            if (itemId === searchId) {
              idMatches = true;
            } else if (itemId === parseInt(searchId)) {
              idMatches = true;
            } else if (String(itemId) === String(searchId)) {
              idMatches = true;
            }
            
            if (!idMatches) return false;
            
                         // If article type is specified, check it matches
             if (articleType) {
               switch (articleType.toLowerCase()) {
                 case 'submitted':
                   return Boolean(item.isSubmittedArticle);
                 case 'weekly':
                   return Boolean(item.isWeeklyNews);
                 default:
                   return true; // Unknown type, allow any match
               }
             }
            
            return true; // No type specified (legacy), return first ID match
          });
          
          if (newsItem) {
            // Check if the article is published
            if (newsItem.published === false) {
              setWelcomeText('Article Not Available');
              setIsLoadingTitle(false);
              return;
            }
            
            setWelcomeText(newsItem.title);
            setIsLoadingTitle(false);
          } else {
            setWelcomeText('News Article Not Found');
            setIsLoadingTitle(false);
          }
          return;
        } catch (err) {
          console.error('Error fetching news data:', err);
          setWelcomeText('Error Loading News Article');
          setIsLoadingTitle(false);
        }
      }
      
      // Rest of your existing pattern matching logic...
      const patternMatch = ROUTE_PATTERNS.find(({ pattern }) => pattern.test(path));
      if (patternMatch) {
        const { config } = patternMatch;
        setWelcomeText(config.text);
        setWelcomeImageClass(config.imageClass);
        if (config.titleClass !== undefined) {
          titleClass = config.titleClass;
        }
        return;
      }

      // Then check exact matches...
      const routeConfig = ROUTE_MAPPINGS[path.toLowerCase()];
      
      if (routeConfig) {
        setWelcomeText(routeConfig.text);
        setWelcomeSubtitle(routeConfig.subtitle || '');
        setWelcomeImageClass(routeConfig.imageClass);
        
        if (routeConfig.titleClass !== undefined) {
          titleClass = routeConfig.titleClass;
        }
      } else {
        // This is likely a 404 route - show appropriate messaging
        setWelcomeText('Page Not Found');
        setWelcomeSubtitle('');
        setWelcomeImageClass('welcome-header-error');
        // Use default titleClass for 404 routes
        titleClass = 'left-bottom-title';
      }
    };

    updateWelcomeText();
  }, [cleanedPath]);

  useEffect(() => {
    setBounce(true);
    const timeout = setTimeout(() => setBounce(false), 1000);
    return () => clearTimeout(timeout);
  }, [cleanedPath]);

  // Handle ESC key and body scroll when ETF chat is open
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isETFChatOpen) {
        setIsETFChatOpen(false);
      }
    };

    if (isETFChatOpen) {
      // Prevent body scrolling
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleEscKey);
      
      return () => {
        // Restore body scrolling
        document.body.style.overflow = 'auto';
        document.removeEventListener('keydown', handleEscKey);
      };
    }
  }, [isETFChatOpen]);

  const handleDaireIconClick = () => {
    const path = cleanedPath.replace(/^\/+|\/+$/g, '');
    
    // If we're on ETFBook page AND environment variable allows it, open ETF Intelligence
    if (path === 'etfbook' && process.env.REACT_APP_SHOW_ETF_DAIRE_INTELLIGENCE === 'true') {
      setIsETFChatOpen(true);
    } else {
      // Regular Daire sidebar for other pages (or ETF page when env var is false)
      if (isDaireSidebarMinimized) {
        setIsDaireSidebarMinimized(false);
      } else {
        setIsDaireSidebarOpen(true);
      }
    }
  };

  return (
    <>
      <section className={`welcome-header ${welcomeImageClass} hide-on-mobile`}>
        <div className='container'>
          <>
            <div className='titlewrapper welcome-title hide-on-mobile'>
              <h1 className={`welcometext ${titleClass}`}>
                {isLoadingTitle ? (
                  <span className="title-loading">
                    <i className="fas fa-spinner fa-spin"></i> {Parser(welcomeText)}
                  </span>
                ) : (
                  Parser(welcomeText)
                )}
              </h1>
              {welcomeSubtitle && (
                <p className="welcome-subtitle" style={{
                  margin: '0 0 0 0',
                  fontSize: '1rem',
                  fontWeight: '400',
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontStyle: 'italic'
                }}>
                  {Parser(welcomeSubtitle)}
                </p>
              )}
              <svg role="presentation" viewBox="0 0 28 95" preserveAspectRatio="none">
                <path d="M28 47.5L-2.08616e-07 95L-2.08616e-07 0L28 47.5Z" fill=""></path>
              </svg>
            </div>
            <div className='header-controls'>
              {!cleanedPath.includes('/daire') && (
                <div className='daire-icon-wrapper'>
                  <Tippy content={cleanedPath.replace(/^\/+|\/+$/g, '') === 'etfbook' && process.env.REACT_APP_SHOW_ETF_DAIRE_INTELLIGENCE === 'true' ? "ETF Intelligence" : "Ask Daire!"}>
                    <div 
                      className={`daire-icon ${bounce ? 'bounce-animation' : ''}`} 
                      onClick={handleDaireIconClick}
                    >
                    </div>
                  </Tippy>
                </div>
              )}
            </div>
          </>
        </div>
      </section>

      <DaireSidebar 
        isOpen={isDaireSidebarOpen}
        onClose={() => {
          setIsDaireSidebarOpen(false);
          setIsDaireSidebarMinimized(false);
        }}
        isMinimized={isDaireSidebarMinimized}
        onMinimizeChange={setIsDaireSidebarMinimized}
        weeklyNewsData={weelklyfilteredNews}
      />

      {isETFChatOpen && (
        <div className="daire-ai-modal-overlay" onClick={() => setIsETFChatOpen(false)}>
          <div className="daire-ai-modal-content" onClick={(e) => e.stopPropagation()}>
            <ETFDaireChat
              isOnDairePage={false}
              hideFullscreen={false}
              onNavigateHome={() => setIsETFChatOpen(false)}
              onShowHelp={() => {
                // Handle help if needed
              }}
              onWelcomeShown={() => {
                // Handle welcome shown if needed
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default WelcomeHeader;
